import { inject, Injectable } from '@angular/core';
import type { IShoppingCartStore } from '@app/interface/cart/shopping-cart-store.interface';
import type { ISubject } from '@app/interface/cart/subject.interface';
import type { IAutoAppliedCoupon } from '@app/interface/content/auto-applied-coupons.interface';
import { AutoApplyCouponType } from '@app/interface/content/auto-apply-coupon-type.enum';
import type { ISeason } from '@app/interface/content/season.interface';
import type { IJobContext } from '@app/interface/job/job-context.interface';
import type { IJobStore } from '@app/interface/job/job-store.interface';
import { angularjs } from '@app/service/migration-helpers/angularjs.injection';

@Injectable({
  providedIn: 'root'
})
export class AutoApplyCouponService {
  jobStore: IJobStore = inject<IJobStore>(angularjs.JobStore);
  shoppingCartStore: IShoppingCartStore = inject<IShoppingCartStore>(angularjs.ShoppingCartStore);

  public getCoupons(): Map<AutoApplyCouponType, IAutoAppliedCoupon> {
    const seasonList: Array<ISeason> = this.getSeasons();
    if (!seasonList) {
      return new Map();
    }
    return this.determineAutoApplyCoupons(seasonList);
  }

  private determineAutoApplyCoupons(seasonList: Array<ISeason>): Map<AutoApplyCouponType, IAutoAppliedCoupon> {
    const coupons: Array<IAutoAppliedCoupon> = new Array<IAutoAppliedCoupon>();

    seasonList.forEach((season: ISeason) => {
      const seasonAutoAppliedCoupons: Array<IAutoAppliedCoupon> = season.autoAppliedCoupons ?? [];
      seasonAutoAppliedCoupons.forEach((autoAppliedCoupon: IAutoAppliedCoupon) => {
        coupons.push(autoAppliedCoupon);
      });
    });

    const activeCoupons: Array<IAutoAppliedCoupon> = coupons.filter((autoAppliedCoupon: IAutoAppliedCoupon) => {
      return this.isCouponActive(autoAppliedCoupon);
    });

    return activeCoupons.reduce((couponTypeToCouponMap, autoAppliedCoupon) => {
      const existingCoupon: IAutoAppliedCoupon | undefined = couponTypeToCouponMap.get(autoAppliedCoupon.type);
      const couponToPersist: IAutoAppliedCoupon = existingCoupon
        ? this.pickAutoApplyCoupon(existingCoupon, autoAppliedCoupon)
        : autoAppliedCoupon;
      couponTypeToCouponMap.set(autoAppliedCoupon.type, couponToPersist);
      return couponTypeToCouponMap;
    }, new Map<AutoApplyCouponType, IAutoAppliedCoupon>());
  }

  private isCouponActive(autoAppliedCoupon: IAutoAppliedCoupon, referenceDate: Date = new Date()): boolean {
    const couponStartTime: Date = autoAppliedCoupon?.startTime
      ? new Date(autoAppliedCoupon?.startTime)
      : new Date(-8640000000000000);
    const couponEndTime: Date = autoAppliedCoupon?.endTime
      ? new Date(autoAppliedCoupon?.endTime)
      : new Date(8640000000000000);
    const referenceTime: number = referenceDate?.getTime();
    return referenceTime >= couponStartTime?.getTime() && referenceTime <= couponEndTime?.getTime?.();
  }

  private pickAutoApplyCoupon(
    existingCoupon: IAutoAppliedCoupon,
    candidateCoupon: IAutoAppliedCoupon
  ): IAutoAppliedCoupon {
    const existingCouponMinimumOrderCost: number = Number(existingCoupon.orderMinCost) || 0;
    const candidateCouponMinimumOrderCost: number = Number(candidateCoupon.orderMinCost) || 0;

    if (
      existingCoupon.type === AutoApplyCouponType.FREE_GIFT &&
      existingCouponMinimumOrderCost === candidateCouponMinimumOrderCost
    ) {
      return this.getGiftSkuNumericValue(existingCoupon) >= this.getGiftSkuNumericValue(candidateCoupon)
        ? existingCoupon
        : candidateCoupon;
    }

    return existingCouponMinimumOrderCost <= candidateCouponMinimumOrderCost ? existingCoupon : candidateCoupon;
  }

  private getGiftSkuNumericValue(autoAppliedCoupon: IAutoAppliedCoupon): number {
    const numericValue: number = Number.parseInt(autoAppliedCoupon.giftSku, 10);
    return Number.isNaN(numericValue) ? 0 : numericValue;
  }

  private getSeasons(): Array<ISeason> {
    const seasons: Array<ISeason> = [];
    const subjects: Array<ISubject> = this.shoppingCartStore.getSubjects();
    subjects?.forEach((subject) => {
      const jobContext: IJobContext = this.jobStore.getJobBySubject(subject);
      seasons.push(jobContext.season);
    });
    return seasons.filter(Boolean);
  }
}
